import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiFileText,
  FiGlobe,
  FiPackage,
  FiPhoneCall,
  FiShield,
} from "react-icons/fi";

import Reveal from "../../components/Reveal/Reveal";
import SEO from "../../components/SEO/SEO";
import StatCounter from "../../components/StatCounter/StatCounter";
import {
  aboutPillars,
  companyInfo,
  exportMarkets,
  pageImages,
  productFamilies,
  services,
  whyChooseReasons,
} from "../../data/siteContent";
import heroVideo from "../../assets/Thamizhi_intro.mp4";

const homeStrengthIcons = [FiShield, FiFileText, FiGlobe, FiPackage];

export default function LandingPage() {
  const stats = [
    {
      value: exportMarkets.length,
      suffix: "+",
      label: "international markets in our current export focus",
    },
    {
      value: productFamilies.length,
      label: "core eco-focused product divisions",
    },
    {
      value: services.length,
      label: "export support services from sourcing to shipment",
    },
    {
      value: 100,
      suffix: "%",
      label: "focus on global buyers, sustainability, and professional coordination",
    },
  ];

  return (
    <>
      <SEO
        title="Premium export and import partner"
        description="Thamizhi International Ventures helps international buyers source eco-friendly bagasse and areca product ranges with dependable communication, documentation, and dispatch support."
        path="/"
        image={pageImages.heroPoster}
        keywords={[
          "eco product exporter",
          "bagasse tableware supplier",
          "international sourcing partner India",
        ]}
      />

      <section className="home-hero">
        <div className="home-hero__media" aria-hidden="true">
          <video
            className="home-hero__video"
            autoPlay
            muted
            loop
            playsInline
            poster={pageImages.heroPoster}
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
          <div className="home-hero__grid" />
        </div>

        <div className="section-shell home-hero__content">
          <Reveal className="home-hero__copy">
            <p className="section-kicker">Export & import specialists based in Tamil Nadu, India</p>
            <h1>Premium, sustainable sourcing built for buyers who value reliability and consistency.</h1>
            <p className="page-summary">
              {companyInfo.name} Supports international buyers with eco-focused product sourcing, transparent documentation, and seamless execution across bagasse, areca, and market-driven export categories.
            </p>

            <div className="button-group">
              <Link to="/products" className="button button-primary">
                Explore products
                <FiArrowRight aria-hidden="true" />
              </Link>
              <Link to="/contact" className="button button-secondary">
                Talk to our team
                <FiArrowRight aria-hidden="true" />
              </Link>
            </div>

            <ul className="hero-points">
              <li>
                <FiCheckCircle aria-hidden="true" />
                Eco-conscious product categories for hospitality, retail, and distribution.
              </li>
              <li>
                <FiCheckCircle aria-hidden="true" />
                Professional buyer communication from inquiry through dispatch.
              </li>
              <li>
                <FiCheckCircle aria-hidden="true" />
                Packaging and documentation handled with export-readiness in mind.
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="section-band section-band--muted">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Performance Highlights</p>
              <h2 className="section-title">Focused on international buyers, export clarity, and sustainable product lines</h2>
            </div>
          </Reveal>

          <div className="stats-grid">
            {stats.map((stat) => (
              <StatCounter
                key={stat.label}
                value={stat.value}
                suffix={stat.suffix}
                label={stat.label}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section-band">
        <div className="section-shell split-layout">
          <Reveal className="overlap-visual">
            <div className="overlap-visual__backdrop" aria-hidden="true" />
            <img src={pageImages.about} alt="About Thamizhi International Ventures" />
          </Reveal>

          <Reveal delay={120}>
            <div className="section-heading">
              <p className="section-kicker">About the company</p>
              <h2 className="section-title">A professional trade partner with a premium, sustainability-led product lens</h2>
            </div>
            <p className="section-copy">
              We combine product curation, packaging awareness, and shipment coordination so
              buyers can work with one team that understands both presentation and export
              discipline.
            </p>

            <div className="content-list">
              {aboutPillars.map((pillar) => (
                <article key={pillar.title} className="content-list__item">
                  <h3>{pillar.title}</h3>
                  <p>{pillar.description}</p>
                </article>
              ))}
            </div>

            <Link to="/about" className="button button-secondary">
              Learn more about us
              <FiArrowRight aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section-band section-band--soft">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Product categories</p>
              <h2 className="section-title">Export-ready ranges aligned to eco-focused demand</h2>
              <p className="section-copy">
                The catalog is structured for real buyer conversations, from molded fiber food
                packaging to natural tableware and request-driven sourcing support.
              </p>
            </div>
          </Reveal>

          <div className="family-grid">
            {productFamilies.map((family, index) => (
              <Reveal key={family.title} className="family-card" delay={index * 80}>
                <img src={family.image} alt={family.title} className="family-card__image" />
                <div className="family-card__content">
                  <p className="eyebrow">{family.accent}</p>
                  <h3>{family.title}</h3>
                  <p>{family.description}</p>
                  <Link to="/products" className="text-link">
                    View category
                    <FiArrowRight aria-hidden="true" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-band">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Why buyers choose us</p>
              <h2 className="section-title">Professional execution beyond the catalog</h2>
            </div>
          </Reveal>

          <div className="feature-grid">
            {whyChooseReasons.slice(0, 4).map((reason, index) => {
              const Icon = homeStrengthIcons[index];

              return (
                <Reveal key={reason.title} className="feature-card" delay={index * 90}>
                  <div className="feature-card__icon">
                    <Icon aria-hidden="true" />
                  </div>
                  <h3>{reason.title}</h3>
                  <p>{reason.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-band section-band--muted">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Export markets in focus</p>
              <h2 className="section-title">Positioned for conversations with importers across key regions</h2>
            </div>
          </Reveal>

          <div className="market-grid">
            {exportMarkets.map((market, index) => (
              <Reveal key={market.name} className="market-card" delay={index * 60}>
                <h3>{market.name}</h3>
                <p>{market.detail}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-band section-band--accent">
        <div className="section-shell cta-band">
          <Reveal>
            <p className="section-kicker">Contact us</p>
            <h2 className="section-title">Ready to discuss a product range, buyer requirement, or export plan?</h2>
            <p className="section-copy">
              Start the conversation with our team and we will guide you through the right
              range, documentation path, and next trade steps.
            </p>
          </Reveal>

          <Reveal className="cta-band__actions" delay={100}>
            <a href={companyInfo.phoneHref} className="button button-primary">
              Call now
              <FiPhoneCall aria-hidden="true" />
            </a>
            <Link to="/contact" className="button button-secondary">
              Contact page
              <FiArrowRight aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
